// Sample patient for the demo (SPEC.md §6.4): about three months of one person's history.
// Writes go to the in-memory demo database only.
import { subDays } from 'date-fns';
import type { DbClient } from '../db/client';
import { toDateKey } from '../lib/schedule';
import { mergeCatalog, saveReport } from '../data/labs';
import { DEFAULT_LAB_TESTS, stdTestId } from '../data/labCatalog';

export async function seedDemo(db: DbClient) {
  await db.upsert('profile', 'profile', {
    full_name: 'Sam Rivera',
    dob: '1968-04-12',
    sex: 'male',
    blood_group: 'B+',
    diagnosis: 'Diabetic nephropathy',
    comorbidities: 'Type 2 diabetes, high blood pressure',
    allergies: 'Penicillin',
    dialysis_start_date: '2023-02-06',
    modality: 'in_center',
    dry_weight_kg: 72.5,
    access_type: 'avf',
    access_location: 'Left forearm',
    access_created_on: '2022-11-20',
    nephrologist_name: 'Dr. A. Patel',
    center_name: 'Riverside Kidney Centre',
    center_phone: '+1 555 0100',
    emergency_contact_name: 'Maria Rivera',
    emergency_contact_phone: '+1 555 0101'
  });
  await db.insert('treatment_schedule', {
    days_of_week: 'mon,wed,fri',
    start_time: '07:30',
    duration_min: 240,
    location: 'Riverside Kidney Centre',
    modality: 'in_center',
    active_from: toDateKey(subDays(new Date(), 400)),
    active_to: null
  });
  for (const [key, value] of Object.entries({ fluid_limit_ml: 1000, currency: 'USD', week_starts_on: 'mon', onboarding_done_at: new Date().toISOString() })) {
    await db.upsert('settings', key, { value: JSON.stringify(value) });
  }
  const weights = await seedSessions(db);
  await seedTracking(db, weights);
  await seedLabs(db);
  await seedMeds(db);
  await seedEverythingElse(db);
}

/** Expenses, nutrition from the dietitian, and journal notes. */
async function seedEverythingElse(db: DbClient) {
  const day = (daysAgo: number) => toDateKey(subDays(new Date(), daysAgo));
  for (let daysAgo = 85; daysAgo >= 1; daysAgo--) {
    const date = subDays(new Date(), daysAgo);
    if ([1, 3, 5].includes(date.getDay())) {
      await db.insert('expenses', { expense_date: toDateKey(date), category: 'transport', description: 'Taxi to the centre and back', amount_minor: 2400 });
    }
    if (daysAgo % 30 === 2) {
      await db.insert('expenses', { expense_date: toDateKey(date), category: 'medication', description: 'Monthly medicines', amount_minor: 18650 });
    }
  }
  for (const [daysAgo, description, amount] of [[80, 'Monthly bloods', 6500], [52, 'Monthly bloods', 6500], [24, 'Monthly bloods + PTH', 9800], [9, 'Repeat potassium', 2500]] as const) {
    await db.insert('expenses', { expense_date: day(daysAgo), category: 'lab', description, amount_minor: amount });
  }
  await db.insert('expenses', { expense_date: day(40), category: 'consultation', description: 'Nephrology clinic', amount_minor: 12000 });

  await db.upsert('nutrition_profile', 'nutrition_profile', {
    target_calories: 2100,
    target_protein_g: 85,
    target_sodium_mg: 2000,
    target_potassium_mg: 2000,
    target_phosphorus_mg: 1000,
    has_diabetes: 1,
    food_allergies: null,
    preferences: 'Likes rice dishes',
    dislikes: 'Fish',
    notes: null
  });
  const restrictions: [string, string, string][] = [
    ['Bananas, oranges and dried fruit', 'high', 'High in potassium. Apples, berries or grapes instead.'],
    ['Cola and processed cheese', 'high', 'High in phosphorus.'],
    ['Salty snacks, pickles, instant noodles', 'medium', 'Salt makes you thirsty.'],
    ['Potatoes', 'medium', 'Peel, cut small and boil in plenty of water (leaching).']
  ];
  for (const [item_name, severity, instructions] of restrictions) await db.insert('nutrition_restrictions', { item_name, severity, instructions, type: 'custom' });
  const plan = await db.insert<{ id: string }>('diet_plans', {
    title: 'Low-potassium plan',
    status: 'active',
    start_date: day(20),
    given_by: 'Riverside dietitian',
    total_calories: 2100,
    total_protein_g: 85,
    total_fluid_ml: 1000,
    total_potassium_mg: 2000,
    general_instructions: 'Take Sevelamer with each meal. Count soup and tea in your fluid.'
  });
  const meals: [string, string, string, string, number][] = [
    ['breakfast', '08:00', 'Two egg omelette with white toast', '1 plate', 0],
    ['breakfast', '08:00', 'Tea with a little milk', '1 small cup', 1],
    ['lunch', '13:00', 'Rice with chicken and green beans', '1 cup rice, palm-sized chicken', 100],
    ['afternoon', '16:00', 'Apple', '1 small', 200],
    ['dinner', '19:30', 'Pasta with olive oil and vegetables', '1 cup', 300]
  ];
  for (const [meal_type, meal_time, food_name, portion_size, offset] of meals) {
    await db.insert('meal_items', { diet_plan_id: plan.id, meal_type, meal_time, food_name, portion_size, sort_order: ['breakfast', 'mid_morning', 'lunch', 'afternoon', 'dinner'].indexOf(meal_type) * 100 + offset % 100 });
  }
  await db.insert('nutrition_notes', { note: 'Dietitian: potassium high in September; cut out bananas and leach potatoes.' });

  const notes: [number, string][] = [
    [30, 'Cramps in the last hour of dialysis twice this week.'],
    [12, 'Question for Dr Patel: can my dry weight be checked again? I feel washed out after sessions.'],
    [3, 'Question: is the itching related to phosphorus?']
  ];
  for (const [daysAgo, body] of notes) await db.insert('notes', { noted_at: subDays(new Date(), daysAgo).toISOString(), body });
}

/** A typical medicine list, three weeks of mostly-logged doses, and vaccinations. */
async function seedMeds(db: DbClient) {
  const meds = [
    { name: 'Sevelamer', strength: '800 mg', form: 'tablet', dose: '2 tablets', category: 'phosphate_binder', times: '["08:00","13:00","19:30"]', with_meals: 1, day_rule: 'every_day' },
    { name: 'Amlodipine', strength: '5 mg', form: 'tablet', dose: '1 tablet', category: 'bp', times: '["21:00"]', with_meals: 0, day_rule: 'non_dialysis_days' },
    { name: 'Calcitriol', strength: '0.25 mcg', form: 'capsule', dose: '1 capsule', category: 'vitamin_d', times: '["21:00"]', with_meals: 0, day_rule: 'specific_days', specific_days: 'mon,wed,fri' },
    { name: 'Erythropoietin', strength: '4000 IU', form: 'injection', dose: null, category: 'esa', times: '[]', with_meals: 0, day_rule: 'dialysis_days', given_at_center: 1 }
  ];
  const ids: string[] = [];
  for (const med of meds) ids.push((await db.insert<{ id: string }>('medications', { active: 1, prescribed_by: 'Dr. A. Patel', ...med })).id);
  const rand = random(11);
  for (let daysAgo = 21; daysAgo >= 1; daysAgo--) {
    const date = toDateKey(subDays(new Date(), daysAgo));
    for (const time of ['08:00', '13:00', '19:30']) {
      const roll = rand(0, 1);
      if (roll < 0.12) continue; // not logged
      const scheduledFor = `${date}T${time}`;
      await db.upsert('med_doses', `${ids[0]}:${scheduledFor}`, {
        medication_id: ids[0],
        scheduled_for: scheduledFor,
        status: roll < 0.2 ? 'skipped' : 'taken',
        logged_at: new Date(`${scheduledFor}:00`).toISOString()
      });
    }
  }
  await db.insert('vaccinations', { vaccine_name: 'Hepatitis B', dose_number: '3', date_given: toDateKey(subDays(new Date(), 150)), next_due_date: toDateKey(subDays(new Date(), -215)), notes: 'Booster per antibody level' });
  await db.insert('vaccinations', { vaccine_name: 'Influenza', dose_number: null, date_given: toDateKey(subDays(new Date(), 30)), next_due_date: null, notes: null });
}

/** Monthly bloods for three months: potassium creeps up, then comes down after diet advice. */
async function seedLabs(db: DbClient) {
  const reports: [number, Record<string, string>, string | null][] = [
    [80, { HB: '10.4', HCT: '31', WBC: '6800', PLT: '210000', UREA: '138', UREA_POST: '44', CREAT: '8.9', K: '5.4', NA: '138', HCO3: '21', CA: '8.6', PHOS: '5.9', ALB: '3.8', FERRITIN: '310', TSAT: '24', PTH: '420', HBSAG: 'Non-reactive', HCV: 'Non-reactive' }, null],
    [52, { HB: '10.8', HCT: '32', WBC: '7100', PLT: '198000', UREA: '145', UREA_POST: '43', CREAT: '9.2', K: '5.9', NA: '137', HCO3: '20', CA: '8.8', PHOS: '6.3', ALB: '3.7' }, null],
    [24, { HB: '11.2', HCT: '34', WBC: '6500', PLT: '205000', UREA: '152', UREA_POST: '41', CREAT: '9.4', K: '6.6', NA: '136', HCO3: '19', CA: '8.5', PHOS: '6.8', ALB: '3.6', FERRITIN: '360', TSAT: '27', PTH: '465' }, 'Doctor reviewed potassium; repeat in two weeks and see the dietitian.'],
    [9, { HB: '11.0', UREA: '140', UREA_POST: '40', K: '5.6', NA: '138', CA: '8.7', PHOS: '5.8', ALB: '3.8' }, 'Repeat after diet changes.']
  ];
  const tests = mergeCatalog(DEFAULT_LAB_TESTS, []);
  for (const [daysAgo, values, notes] of reports) {
    const byId = new Map(Object.entries(values).map(([code, value]) => [stdTestId(code), value]));
    await saveReport(db, undefined, { result_date: toDateKey(subDays(new Date(), daysAgo)), lab_name: 'Riverside Lab', notes }, byId, tests, []);
  }
}

/** Deterministic pseudo-random numbers, so the demo looks the same every time. */
function random(seed: number) {
  let state = seed;
  return (min: number, max: number) => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return min + (state / 4294967296) * (max - min);
  };
}

const round = (value: number, step: number) => Math.round(value / step) * step;
const HISTORY_DAYS = 90;
const kg = (value: number) => Math.round(value * 10) / 10;

/** Three months of Mon/Wed/Fri sessions ending yesterday, with one missed and one ended early. */
async function seedSessions(db: DbClient): Promise<Map<string, number>> {
  /** Post-session weight by date, for the home weights in between. */
  const posts = new Map<string, number>();
  const rand = random(42);
  const dry = 72.5;
  let lastPost = 72.8;
  let lastDate: Date | null = null;
  let index = 0;
  for (let daysAgo = HISTORY_DAYS; daysAgo >= 1; daysAgo--) {
    const day = subDays(new Date(), daysAgo);
    if (![1, 3, 5].includes(day.getDay())) continue;
    const date = toDateKey(day);
    index++;
    if (index === 5) {
      await db.insert('sessions', { session_date: date, modality: 'in_center', status: 'missed', missed_reason: 'Flu, stayed home' });
      continue;
    }
    const gapDays = lastDate ? Math.round((day.getTime() - lastDate.getTime()) / 86_400_000) : 2;
    const pre = kg(lastPost + (gapDays >= 3 ? rand(2.6, 3.4) : rand(1.5, 2.4)));
    const endedEarly = index === 11;
    const post = kg(endedEarly ? pre - rand(1.0, 1.4) : dry + rand(-0.2, 0.5));
    const session = await db.insert<{ id: string }>('sessions', {
      session_date: date,
      modality: 'in_center',
      status: endedEarly ? 'ended_early' : 'completed',
      start_time: '07:30',
      end_time: endedEarly ? '10:05' : '11:30',
      duration_min: endedEarly ? 155 : 240,
      pre_weight_kg: pre,
      post_weight_kg: post,
      dry_weight_kg: dry,
      uf_goal_ml: round((pre - dry) * 1000, 100),
      uf_achieved_ml: round((pre - post) * 1000, 50),
      early_end_reason: endedEarly ? 'Low blood pressure and cramps' : null
    });
    const at = (time: string) => new Date(`${date}T${time}:00`).toISOString();
    const vital = (phase: string, time: string, systolic: number, diastolic: number, pulse: number) =>
      db.insert('session_vitals', {
        session_id: session.id,
        phase,
        recorded_at: at(time),
        bp_systolic: Math.round(systolic),
        bp_diastolic: Math.round(diastolic),
        pulse: Math.round(pulse)
      });
    await vital('pre', '07:30', rand(142, 166), rand(80, 92), rand(72, 88));
    await vital('intra', '09:00', rand(128, 146), rand(74, 86), rand(70, 84));
    if (endedEarly) await vital('intra', '09:50', 92, 58, 96);
    else await vital('intra', '10:30', rand(120, 138), rand(70, 82), rand(70, 82));
    await vital('post', endedEarly ? '10:05' : '11:30', endedEarly ? 98 : rand(120, 136), endedEarly ? 62 : rand(70, 80), rand(70, 86));
    lastPost = post;
    lastDate = day;
    posts.set(date, post);
  }
  return posts;
}

/** Three months of drinks, morning weights, home blood pressure, symptoms and access checks. */
async function seedTracking(db: DbClient, posts: Map<string, number>) {
  const rand = random(7);
  const now = Date.now();
  const at = (day: Date, hours: number, minutes = 0) => {
    const moment = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes);
    return moment.getTime() <= now ? moment.toISOString() : null;
  };
  const kinds = ['water', 'water', 'tea', 'water', 'soup', 'water'];
  let lastPost = 72.8;
  for (let daysAgo = HISTORY_DAYS; daysAgo >= 0; daysAgo--) {
    const day = subDays(new Date(), daysAgo);
    const date = toDateKey(day);
    const sessionDay = [1, 3, 5].includes(day.getDay());

    // Drinks: usually near the 1000 mL limit, sometimes over (more on long-gap weekends).
    const drinks = Math.round(rand(4, 7));
    for (let index = 0; index < drinks; index++) {
      const takenAt = at(day, 7 + Math.round((index * 14) / drinks), Math.round(rand(0, 50)));
      if (!takenAt) continue;
      const amount = round(rand(day.getDay() === 6 ? 180 : 120, day.getDay() === 6 ? 300 : 230), 10);
      await db.insert('fluid_entries', { taken_at: takenAt, amount_ml: amount, kind: kinds[index % kinds.length] });
    }

    // A morning weight on days without dialysis, gaining since the last session.
    if (!sessionDay) {
      const measuredAt = at(day, 7, 10);
      if (measuredAt) await db.insert('weight_entries', { measured_at: measuredAt, weight_kg: kg(lastPost + rand(0.8, 1.6)), context: 'morning' });
    } else if (posts.has(date)) {
      lastPost = posts.get(date)!;
    }

    if (daysAgo % 2 === 0) {
      const measuredAt = at(day, 8, 30);
      if (measuredAt) {
        await db.insert('bp_entries', {
          measured_at: measuredAt,
          systolic: Math.round(rand(134, 158)),
          diastolic: Math.round(rand(78, 90)),
          pulse: Math.round(rand(68, 84))
        });
      }
    }

    if (daysAgo % 3 === 1) {
      const checkedAt = at(day, 21);
      const redness = daysAgo === 10;
      if (checkedAt) {
        await db.insert('access_checks', {
          checked_at: checkedAt,
          access_type: 'avf',
          thrill_present: 1,
          bruit_present: 1,
          redness: redness ? 1 : 0,
          swelling: 0,
          pain: redness ? 1 : 0,
          bleeding_prolonged: 0,
          discharge: 0,
          fever: 0,
          note: redness ? 'Slight redness near the top needle site; showed the nurse.' : null
        });
      }
    }
  }

  const symptoms: [number, string, number, string | null][] = [
    [40, 'Itching', 2, 'Worse at night'],
    [36, 'Fatigue', 3, 'Tired after dialysis'],
    [33, 'Cramps', 3, 'Legs, last hour of dialysis'],
    [29, 'Itching', 3, null],
    [26, 'Restless legs', 2, null],
    [22, 'Cramps', 4, 'Needed saline'],
    [19, 'Fatigue', 2, null],
    [15, 'Itching', 2, null],
    [12, 'Headache', 2, 'After dialysis'],
    [8, 'Cramps', 2, null],
    [5, 'Itching', 1, null],
    [2, 'Fatigue', 3, null]
  ];
  for (const [daysAgo, symptom, severity, note] of symptoms) {
    await db.insert('symptom_entries', { occurred_at: at(subDays(new Date(), daysAgo), 20)!, symptom, severity, note });
  }
}
